import { Component, Input } from '@angular/core';
import { GridRegistrationModel } from '@bo-client/activation-management/grid-registration/model/grid-registration.model';
import { ConsumerModel } from '@bo-client/activation-management/grid-registration/model/consumer.model';
import { PowerInverterTypeDisplayNames } from '@bo-client/activation-management/grid-registration/model/producer.model';
import { storageTotal } from '@bo-client/activation-management/grid-registration/storage-totals.util';
import { MetaService } from '@bo-client/core/api/meta-service';

/**
 * Technische Angaben eines Speichers des **neuen** Speicherprozesses (v2) für Zusammenfassungen.
 *
 * Bewusst eine eigene Darstellung statt `app-producers-summary` / `app-consumers-summary`: die
 * geteilten Zusammenfassungen beschreiben Erzeugungseinheiten einer PV-Anlage bzw. eine
 * Ladeeinrichtung. Sie brächten die fachlich falsche Überschrift „Erzeugungseinheiten" mit und
 * kennen weder die Wechselrichter-Begriffe des v2-Speicherprozesses noch die Entladeleistung.
 *
 * Verwendet von der Zusammenfassung des Inbetriebsetzungsprotokolls
 * (`GrPlaceInServiceStorageSummaryComponent`), der Bestätigungsseite der Techniker-Zuweisung und
 * dem externen Netzanmeldungsformular.
 */
@Component({
  selector: 'app-storage-v2-technical-summary',
  templateUrl: './storage-v2-technical-summary.component.html',
  styleUrls: ['../../../../shared/summaries/css-summary.component.scss']
})
export class StorageV2TechnicalSummaryComponent {
  @Input() gr: GridRegistrationModel;

  protected readonly powerInverterTypeDisplayNames = PowerInverterTypeDisplayNames;

  constructor(protected metaService: MetaService) {
    // Empty
  }

  /**
   * Der Speicher des v2-Prozesses verteilt sich über drei Sammlungen: `storages[0]` trägt die
   * Speicherdaten, `producers[]` die Wechselrichter (der erste zusätzlich die Entladeleistung) und
   * `consumers[0]` den Speicher als Verbrauchseinrichtung (Lade- und Wechselrichterleistung, §14a).
   */
  protected get firstConsumer(): ConsumerModel | undefined {
    return this.gr?.consumers?.[0];
  }

  protected get dischargePower(): number | undefined {
    return this.gr?.producers?.[0]?.dischargePower;
  }

  /** Gesamtwerte über alle Wechselrichter (`Anzahl × Einzelwert` je Gerät). */
  protected get totalActivePower(): number {
    return this.sumOverProducers((acPower) => acPower.acActivePowerMax);
  }

  protected get totalApparentPower(): number {
    return this.sumOverProducers((acPower) => acPower.acApparentPowerMax);
  }

  /** Anzahl der Speichersysteme; alle Speicher-Einzelwerte gelten pro System. */
  private get storageCount(): number | undefined {
    return this.gr?.storages?.[0]?.count;
  }

  /** Gesamtwert eines Speicher-Einzelwerts (`Anzahl × Einzelwert`), `null` bei fehlender Angabe. */
  protected total(valuePerStorage: unknown): number | null {
    return storageTotal(this.storageCount, valuePerStorage);
  }

  private sumOverProducers(pick: (producer: GridRegistrationModel['producers'][number]) => number): number {
    return (this.gr?.producers ?? []).reduce(
      (total, producer) => total + (Number(pick(producer)) || 0) * (Number(producer.count) || 0),
      0
    );
  }
}
